import { Typography } from '@/components/UI/Typography'
import { countCodePoints, splitCodePoints, truncateCodePoints } from '@/lib/string/codePoints'
import { type KeyboardEvent, type Ref, type SyntheticEvent, memo, useId, useRef } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  /** 枠の数（= 正解の文字数） */
  length: number
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  disabled?: boolean
  /** react-hook-form の Controller から渡す field.ref（setFocus / shouldFocusError 用） */
  inputRef?: Ref<HTMLInputElement>
}

/**
 * 1 行に並べる枠の上限。
 * Dialog（compact = Mantine size sm）の内側はスマホで約 310px しかなく、8 枠だと 1 枠が約 33px と細長くなる。
 * Auth 画面の OTP（6 桁）と同じ 6 枠にすると 1 枠が約 47px で見た目が揃い、タップもしやすい。
 */
const MAX_COLUMNS = 6

/**
 * 1文字入力にする文字数の上限（2 行ぶん）。
 * 管理画面は正解ワードを 255 文字まで許すため、長い正解でこの枠が何十行にもならないよう、
 * これを超える場合は呼び出し側で従来のテキストボックスに戻す。
 */
export const MAX_CHAR_INPUT_LENGTH = MAX_COLUMNS * 2

/**
 * 入力値の正規化（IME 変換中以外に適用）。
 * - 空白（半角・全角スペース、NBSP、タブ、改行など `\s` に該当する文字）はすべて取り除く。
 *   空白を残すと枠が空のまま埋まって見え、送信しても不正解になる理由がユーザーに伝わらない。
 *   スペースを含む正解は運用上作らない前提（仕様書「決定済み」参照）
 * - コードポイント単位で length 文字に切り詰める（バックエンドの mb_strlen と数え方を揃える）
 */
const normalize = (value: string, length: number): string => truncateCodePoints(value.replace(/\s/g, ''), length)

/**
 * 正解の文字数ぶんの1文字枠を並べたキーワード入力欄（#2591）。
 *
 * 枠ごとに input を分けると日本語 IME の変換（未確定文字列）が枠をまたげないため、
 * 入力は透明な input 1つで受け、枠は表示専用にする。IME 変換中は切り詰めず、確定後に length 文字へ揃える。
 * 見た目はバックエンド Auth 画面の認証コード入力（templates/v2/auth/login/otp.html.twig の .otp-box）に揃える。
 */
export const KeywordCharInput = memo(({ length, value, onChange, onBlur, disabled = false, inputRef }: Props) => {
  const { t } = useTranslation()
  const descriptionId = useId()
  const composingRef = useRef(false)

  const chars = splitCodePoints(value)
  // 次に文字が入る枠（カーソル位置）。全枠埋まっているときは最後の枠を強調する
  const activeIndex = Math.min(chars.length, length - 1)

  // IME の変換確定 Enter でフォームが送信されないようにする。
  // Chrome は変換中の Enter で暗黙の送信をしないが、Safari では送信されることがある
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && (e.nativeEvent.isComposing || e.keyCode === 229)) {
      e.preventDefault()
    }
  }

  // キャレットは末尾に固定する。
  // 枠の強調は「次に入る位置（末尾）」を示しているため、透明 input のキャレットが途中にあると
  // Backspace や入力が UI と違う位置に効いてしまう。
  // ただし範囲選択（「すべて選択」→ Backspace で全消し）は妨げない。IME 変換中に動かすと変換が壊れるので変換中は触らない
  const pinCaretToEnd = (e: SyntheticEvent<HTMLInputElement>) => {
    if (composingRef.current) return
    const el = e.currentTarget
    if (el.selectionStart !== el.selectionEnd) return
    const end = el.value.length
    if (el.selectionStart !== end) {
      el.setSelectionRange(end, end)
    }
  }

  return (
    // フォーカス中の枠の強調は state ではなく CSS の :focus-within（group-focus-within）で付ける。
    // state で持つと、disabled 化で blur が発火しないブラウザ（Chrome）で
    // 送信中 → エラーで再有効化されたときに強調が残る。CSS ならフォーカスが外れた時点で自動的に消える
    <div className="group relative">
      {/*
        表示用の枠。Auth 画面は flex-1 で横幅いっぱいに均等割りしているが、キーワードは文字数が可変なので
        grid にして 1 行最大 MAX_COLUMNS 枠で折り返す。
        タップは上に重ねた透明 input が受けるため、この要素にイベントは付けない。
        色は意図的に Auth 画面の OTP 入力（border-gray-300 / focus: blue-500）と同じ固定色にしている
        （キャンペーンのテーマ色 `main` を使う規約の例外。仕様書「決定済み」参照）
      */}
      <div
        className={['grid w-full gap-1.5', disabled ? 'opacity-50' : ''].join(' ')}
        style={{ gridTemplateColumns: `repeat(${Math.min(length, MAX_COLUMNS)}, minmax(0, 1fr))` }}
        aria-hidden="true"
      >
        {Array.from({ length }, (_, i) => (
          <Typography
            // biome-ignore lint/suspicious/noArrayIndexKey: 固定長の表示専用スロットで並び替え・挿入は起きない
            key={i}
            variant="h3"
            component="span"
            className={[
              'flex h-16 min-w-0 items-center justify-center rounded-lg border bg-white text-center text-black transition',
              'border-gray-300',
              i === activeIndex
                ? 'group-focus-within:border-blue-500 group-focus-within:ring-2 group-focus-within:ring-blue-200'
                : '',
            ].join(' ')}
          >
            {chars[i] ?? ''}
          </Typography>
        ))}
      </div>

      {/* 枠は aria-hidden なので、必要な文字数は読み上げ専用テキストで伝える */}
      <span id={descriptionId} className="sr-only">
        {t('spot_detail.quiz.keyword_char_count', '正解は{{count}}文字です', { count: length })}
      </span>

      {/* 実際に入力を受ける input。枠の上に重ねて透明にする（display:none だとソフトウェアキーボードが出ない） */}
      <input
        ref={inputRef}
        data-testid="keyword-char-input"
        type="text"
        value={value}
        aria-label={t('spot_detail.quiz.keyword_placeholder', 'キーワードを入力')}
        aria-describedby={descriptionId}
        autoComplete="off"
        autoCapitalize="off"
        spellCheck={false}
        disabled={disabled}
        onFocus={pinCaretToEnd}
        onBlur={(e) => {
          // compositionend が届かないまま blur した場合（IME の中断など）に「変換中」のまま残さない
          if (composingRef.current) {
            composingRef.current = false
            onChange(normalize(e.currentTarget.value, length))
          }
          onBlur?.()
        }}
        onSelect={pinCaretToEnd}
        onKeyDown={handleKeyDown}
        onCompositionStart={() => {
          composingRef.current = true
        }}
        onCompositionEnd={(e) => {
          composingRef.current = false
          onChange(normalize(e.currentTarget.value, length))
        }}
        onChange={(e) => {
          // IME 変換中は未確定文字列が length を超えることがあるため切り詰めない
          onChange(composingRef.current ? e.target.value : normalize(e.target.value, length))
        }}
        className="absolute inset-0 h-full w-full cursor-text opacity-0"
      />
    </div>
  )
})

/** 1文字入力にできる文字数か（1 以上 MAX_CHAR_INPUT_LENGTH 以下の整数） */
export const isCharInputLength = (answerLength: number | null | undefined): answerLength is number =>
  typeof answerLength === 'number' &&
  Number.isInteger(answerLength) &&
  answerLength > 0 &&
  answerLength <= MAX_CHAR_INPUT_LENGTH

/**
 * 1文字入力の値が正解の文字数と一致しているか。
 * 値は normalize 済み（空白なし）なので、コードポイント数をそのまま比べる
 */
export const matchesAnswerLength = (value: string, answerLength: number): boolean =>
  countCodePoints(value) === answerLength
